import { ReadStrengthTag } from "./ReadStrengthTag";
import { SafeAnalystMarkdown } from "./SafeAnalystMarkdown";
import { StorylineCutRows } from "./StorylineCutRows";
import { StorylineExhibitLink } from "./StorylineExhibitLink";
import { StorylineFigure } from "./StorylineFigure";
import { StorylineSources } from "./StorylineSources";
import { buildFactNumbering } from "./analystFactNumbering";
import {
  readableCuts,
  storylineSources,
} from "@/features/studies/report/storyline";

import type {
  MetricResult,
  PublicStoryline,
  PublicStorylineFinding,
} from "@digital-twins/shared-types/reporting";
import type { StorylineExhibitLinkState } from "@/features/studies/report/storyline";
import type { FindingPlot } from "@/features/studies/report/storylinePlots";

interface StorylineFindingProps {
  storyline: PublicStoryline;
  finding: PublicStorylineFinding;
  index: number;
  linkState: StorylineExhibitLinkState;
  metricResults?: Readonly<Record<string, MetricResult>> | null;
  /** The finding's embedded plot, when it is one of the selected few. */
  plot?: FindingPlot | null;
  onOpenSlide?: (slideId: string) => void;
}

/** Number-badge visual, the analyst evidence rows' token (`AnalystEvidence`). */
const FINDING_NUMBER_BADGE_CLASS =
  "mt-1 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-xs font-semibold leading-none bg-muted text-foreground";

function noFactToOpen() {}

/**
 * One kept finding: its headline and read, the body, its plot when it has
 * one, the cuts it cites inline (a plotted cut's rows move into the sources
 * disclosure), its slide link and where its figures come from.
 */
export function StorylineFinding(props: StorylineFindingProps) {
  const {
    storyline,
    finding,
    index,
    linkState,
    metricResults,
    plot,
    onOpenSlide,
  } = props;

  const cuts = readableCuts(storyline, finding.cutRequestIds ?? []);
  const sources = storylineSources(storyline, finding.evidenceIds, metricResults);
  const plottedCutIds = new Set(
    plot?.kind === "cut" ? plot.cuts.map((cut) => cut.requestId) : [],
  );
  const inlineCuts = cuts.filter((cut) => !plottedCutIds.has(cut.requestId));
  const plottedCuts = cuts.filter((cut) => plottedCutIds.has(cut.requestId));

  const figure = plot ? (
    <StorylineFigure plot={plot} findingIndex={index} headline={finding.headline} />
  ) : null;
  const disclosedCutRows =
    plottedCuts.length > 0
      ? plottedCuts.map((cut) => <StorylineCutRows key={cut.requestId} cut={cut} />)
      : null;

  return (
    <li
      className="flex gap-3"
      data-testid={`storyline-finding-${index}`}
    >
      <span className={FINDING_NUMBER_BADGE_CLASS} aria-hidden="true">
        {index + 1}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <h4
            className="min-w-0 flex-1 font-sans text-base font-semibold leading-snug text-foreground"
            data-testid={`storyline-finding-headline-${index}`}
          >
            {finding.headline}
          </h4>
          {finding.readStrength ? (
            <ReadStrengthTag
              readStrength={finding.readStrength}
              dataTestId={`storyline-read-${index}`}
            />
          ) : null}
        </div>
        <SafeAnalystMarkdown
          content={finding.body}
          numbering={buildFactNumbering(finding.body, [])}
          onOpenFact={noFactToOpen}
        />
        {figure}
        {inlineCuts.map((cut) => (
          <StorylineCutRows key={cut.requestId} cut={cut} />
        ))}
        <StorylineExhibitLink
          state={linkState}
          findingIndex={index}
          onOpenSlide={onOpenSlide}
        />
        <StorylineSources
          rows={sources}
          findingIndex={index}
          cutRows={disclosedCutRows}
        />
      </div>
    </li>
  );
}
